"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useCourseContext } from "~/contexts/CourseContext";
import { useAppContext } from "~/contexts/AppContext";

declare global {
  interface Window {
    dataLayer?: unknown[];
    __gaLastFiredPath?: string;
  }
}

const MASTER_ID = process.env.NEXT_PUBLIC_MEASUREMENT_ID;

type Props = {
  // Fed from courseData in the course layout. Preferred over CourseContext,
  // which is undefined until course images also load — by then this effect has
  // already fired page_view (once per path, de-duped), so reading the context
  // would drop course_id/course_name from the page_view. Omitted at the root
  // layout, where there is no course.
  courseId?: string;
  courseName?: string;
  measurementId?: string | null;
};

const GAPageViewTracker = ({
  courseId: courseIdProp,
  courseName: courseNameProp,
  measurementId: measurementIdProp,
}: Props = {}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { course } = useCourseContext();
  const { entity } = useAppContext();

  const search = searchParams?.toString() ?? "";
  const entityId = entity?.id;
  const entityName = entity?.name;
  // Prefer the props (available immediately) over the image-dependent context.
  const courseId = courseIdProp ?? course?.id;
  const courseName = courseNameProp ?? course?.name;
  const courseMeasurementId =
    measurementIdProp ??
    (course as { measurementGAId?: string | null } | undefined)
      ?.measurementGAId;

  useEffect(() => {
    if (typeof window === "undefined" || !pathname) return;

    // Two trackers render: one in the root layout (no course props) and one in
    // the course layout (course props). On a course route the root one must
    // STAND DOWN — otherwise, because the course subtree is delayed by its
    // server data fetch, the root tracker fires first with no course and writes
    // course_name:"" (dropped → absent), wiping what CourseGAConfig set. Course
    // routes start with the course id (a UUID); only the course-aware tracker
    // (courseId present) handles them.
    const isCourseRoute =
      /^\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
        pathname
      );
    if (!courseId && isCourseRoute) return;

    // Read the URL from window.location (updated synchronously on navigation)
    // rather than useSearchParams, which settles a render late.
    const fullPath = window.location.pathname + window.location.search;

    const sendTo = [MASTER_ID, courseMeasurementId].filter(Boolean);

    window.dataLayer = window.dataLayer || [];
    function gtag(this: unknown, ..._args: unknown[]) {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    }

    // Keep page_location current for EVERY event on EVERY navigation — including
    // query-only ones like the My Tee Box ?section= tabs (Offers Received/Sent).
    // We deliberately do NOT re-fire page_view for query changes (that
    // double-counts), but without this the events fired on those tabs (tab_*,
    // btn_mtb_offer_*, …) would report the PREVIOUS page's URL.
    gtag("set", { page_location: window.location.href, page_path: fullPath });

    // Re-assert the config defaults for THIS navigation. The master tag lives
    // for the whole session, so its course goes stale as you move between pages
    // — we set it deterministically here on every navigation (rather than from a
    // mount/unmount effect, which races and clears it). These config defaults
    // ride on EVERY hit, including gtag.js's automatically-collected events
    // (session_start, user_engagement, scroll). Empty string clears the course
    // when leaving a course for a no-course page (home/facility).
    const defaults: Record<string, unknown> = {
      course_id: courseId ?? "",
      course_name: courseName ?? "",
      send_page_view: false,
    };
    if (entityId) defaults.entity_id = entityId;
    if (entityName) defaults.entity_name = entityName;
    if (MASTER_ID) gtag("config", MASTER_ID, defaults);
    if (courseMeasurementId) gtag("config", courseMeasurementId, defaults);

    // Fire the page_view EVENT once per pathname. Query-only changes don't
    // re-fire it (avoids the double-count from useSearchParams settling in two
    // steps), but page_location is already updated above so those tabs'
    // events are attributed to the right URL.
    if (window.__gaLastFiredPath === pathname) return;
    window.__gaLastFiredPath = pathname;

    const params: Record<string, unknown> = {
      page_path: fullPath,
      page_location: window.location.href,
      page_title: document.title,
    };
    if (sendTo.length) params.send_to = sendTo;
    if (entityId) params.entity_id = entityId;
    if (entityName) params.entity_name = entityName;
    if (courseId) params.course_id = courseId;
    if (courseName) params.course_name = courseName;

    gtag("event", "page_view", params);
  }, [
    pathname,
    search,
    entityId,
    entityName,
    courseId,
    courseName,
    courseMeasurementId,
  ]);

  return null;
};

export default GAPageViewTracker;
